import { Lock } from 'lucide-react';
import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { INTERNAL_CLASSES, type InternalClass } from '../internal-class';
import { setExpenseClass } from '../reports/api-parity';

const PROBLEMS = ['CLASS_NOT_ALLOWED', 'MONTH_CLOSED'] as const;
type ClassProblem = (typeof PROBLEMS)[number] | 'other';

/** Saves one expense's class; on refusal the shown value goes back to the saved one. */
export function useClassSetter<T>(
  expenseId: string,
  saved: InternalClass | null,
  onSaved: (view: T) => void,
) {
  const [shown, setShown] = useState<InternalClass | null>(saved);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<ClassProblem | null>(null);
  useEffect(() => setShown(saved), [saved]);
  const choose = async (next: InternalClass | null): Promise<void> => {
    if (busy || next === shown) return;
    setShown(next);
    setBusy(true);
    setProblem(null);
    const out = await setExpenseClass<T>(expenseId, next);
    setBusy(false);
    if (out.ok) {
      onSaved(out.value);
      return;
    }
    setShown(saved);
    setProblem(PROBLEMS.find((code) => code === out.code) ?? 'other');
  };
  return { shown, busy, problem, choose } as const;
}

export function ClassProblemLine({ problem }: { readonly problem: ClassProblem | null }) {
  const { t } = useTranslation('expenses-parity');
  if (problem === null) return null;
  return (
    <p role="alert" className="text-destructive text-sm">
      {t('class.failed', { why: t(`class.problems.${problem}`) })}
    </p>
  );
}

/**
 * «Classificazione interna» on the expense's card (U1 board 2.1, 2.4): six segments, «Nessuna»
 * included; a tap saves it, as the manual status does. Shown only when the view carries the class,
 * which the server sends to Gestione and the note's approvers only.
 */
export function ClassControl<T>({
  expenseId,
  value,
  onSaved,
}: {
  readonly expenseId: string;
  readonly value: InternalClass | null;
  readonly onSaved: (view: T) => void;
}) {
  const { t } = useTranslation('expenses-parity');
  const name = useId();
  const setter = useClassSetter(expenseId, value, onSaved);
  const options: readonly (InternalClass | null)[] = [null, ...INTERNAL_CLASSES];
  return (
    <section className="flex flex-col gap-2 rounded-md border p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-sm font-semibold" id={`${name}-title`}>
          {t('class.title')}
        </h3>
        <span className="text-muted-foreground inline-flex items-center gap-1 text-xs">
          <Lock className="size-3" aria-hidden />
          {t('class.only')}
        </span>
      </div>
      <div
        role="radiogroup"
        aria-labelledby={`${name}-title`}
        aria-busy={setter.busy}
        className="grid grid-cols-6 overflow-hidden rounded-md border"
      >
        {options.map((option) => {
          const label = option ?? t('class.none');
          const checked = setter.shown === option;
          return (
            <label
              key={label}
              className={cn(
                'flex min-h-11 cursor-pointer items-center justify-center border-l text-sm font-semibold first:border-l-0 has-[:focus-visible]:ring-2',
                // #951: the chosen segment wears the design system's selected state (blue), never
                // the action colour: «Nessuna» chosen is a value, not a call to act.
                checked ? 'bg-selected text-selected-foreground' : 'hover:bg-muted',
              )}
            >
              <input
                type="radio"
                className="sr-only"
                name={name}
                checked={checked}
                disabled={setter.busy}
                onChange={() => void setter.choose(option)}
              />
              {label}
            </label>
          );
        })}
      </div>
      <p className="text-muted-foreground text-sm">{t('class.hint')}</p>
      {setter.busy ? (
        <p role="status" className="text-muted-foreground text-sm">
          {t('class.saving')}
        </p>
      ) : null}
      <ClassProblemLine problem={setter.problem} />
    </section>
  );
}
